import type {Action} from 'platform/components';
import {Box, HStack, Show, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import type {ReactNode} from 'react';

import {isNil} from 'ramda';

import type {
  ConfiguratorBundleResponseBody,
  EvaluateConfigurationResponseBody,
  PriceSnapshotResponseBody,
  PriceBasis,
} from '@dms/api/new-vehicle';
import {
  ConfiguratorHero,
  ConfiguratorPriceBar,
  ConfiguratorStepper,
  SummaryRail,
  type ConfiguratorHeroChip,
  type ConfiguratorHeroProps,
  type ConfiguratorStep,
} from '@dms/features/new-vehicle';
import i18n from '@dms/i18n';

import {ColourSwatch} from '../../../components/ColourSwatch/ColourSwatch';
import {getConfiguratorPriceState} from '../utils/getConfiguratorPriceState';
import {isStandardFeature} from '../utils/isStandardFeature';

const t = (key: string, defaultValue: string) => i18n.t(key, {defaultValue});

export type ConfiguratorLayoutProps = {
  basis?: PriceBasis;
  isBundleError?: boolean;
  children: ReactNode;
  steps: ConfiguratorStep[];
  currentStepIndex: number;
  onStepChange: (index: number) => void;
  bundle?: ConfiguratorBundleResponseBody;
  evaluation?: EvaluateConfigurationResponseBody;
  isEvaluationEligible: boolean;
  isFetching: boolean;
  isSettled: boolean;
  canViewCost: boolean;
  trimId: string;
  variantId: string;
  colourId: string;
  upholsteryId: string;
  featureIds: string[];
  packageIds: string[];
  context: {makeName: string; modelName: string; modelYearName: string};
  status?: ConfiguratorHeroProps['status'];
  /** Reason the user cannot continue yet; also disables the Continue action. */
  blocker?: string;
  leadingActions?: Action[];
  trailingActions?: Action[];
  /** Hide the default Back / Continue navigation (e.g. on Review). */
  hideStepNavigation?: boolean;
  /** Hide the side rail and the content card (e.g. on Review which renders its own layout). */
  hideRail?: boolean;
  /** Frozen snapshot used only while the saved configuration intent is unchanged. */
  snapshot?: PriceSnapshotResponseBody;
  banner?: ReactNode;
};

// eslint-disable-next-line complexity -- Pre-existing violation
export function ConfiguratorLayout(props: ConfiguratorLayoutProps) {
  const trim = props.bundle?.trims.find(({id}) => id === props.trimId);
  const variant = props.bundle?.variants.find(({id}) => id === props.variantId);
  const engine = props.bundle?.engines.find(({id}) => id === variant?.engineId);
  const colour = props.bundle?.colours.find(({id}) => id === props.colourId);
  const upholstery = props.bundle?.upholsteries.find(({id}) => id === props.upholsteryId);
  // STANDARD equipment a package or rule carried into the selection is included, not an option (D72).
  const standardFeatureIds = new Set(
    (props.bundle?.features ?? [])
      .filter((feature) => isStandardFeature(feature, props.trimId))
      .map(({id}) => id)
  );
  const optionFeatureCount = props.featureIds.filter((id) => !standardFeatureIds.has(id)).length;
  const extrasCount = optionFeatureCount + props.packageIds.length;
  const proposed = props.evaluation
    ? {
        net: props.evaluation.proposedSellingPriceNet ?? undefined,
        gross: props.evaluation.proposedSellingPriceGross ?? undefined,
      }
    : undefined;
  const isPending =
    !props.snapshot && props.isEvaluationEligible && (props.isFetching || !props.isSettled);
  const currency = props.snapshot?.currency ?? props.bundle?.currency ?? '';
  const rate = props.snapshot?.vatRate ?? props.bundle?.vatRate;
  const priceState = getConfiguratorPriceState(props.basis, props.isBundleError ?? false);
  const {railPrice, barPrice, caption} = match(priceState)
    .with({kind: 'known'}, ({basis}) => ({
      railPrice: {
        basis,
        totals: props.evaluation?.totals,
        proposed,
        cost:
          props.canViewCost && props.evaluation?.totals.costNet
            ? {costNet: props.evaluation.totals.costNet}
            : undefined,
        isPending,
      },
      barPrice: {
        net: props.snapshot?.totalNet ?? props.evaluation?.totals.net,
        gross: props.snapshot?.totalGross ?? props.evaluation?.totals.gross,
        basis,
        isPending: isNil(props.snapshot) && isPending,
      },
      caption: {basis, rate},
    }))
    .with({kind: 'loading'}, () => ({
      railPrice: {isPending: true},
      barPrice: {isPending: true},
      caption: undefined,
    }))
    .with({kind: 'unavailable'}, ({displayBasis}) => ({
      railPrice: {isPending: false},
      barPrice: {
        net: props.snapshot?.totalNet,
        gross: props.snapshot?.totalGross,
        basis: displayBasis,
        isPending: false,
      },
      caption: props.snapshot ? {basis: displayBasis, rate: props.snapshot.vatRate} : undefined,
    }))
    .exhaustive();

  const chips: ConfiguratorHeroChip[] = [
    ...(engine
      ? [
          {
            caption: t('entity.newVehicle.labels.drive', 'Drive'),
            label: `${engine.powerKw} kW · ${engine.name}`,
          },
        ]
      : []),
    ...(colour
      ? [
          {
            label: colour.name,
            leading: <ColourSwatch name={colour.name} hex={colour.hex} size="dot" />,
          },
        ]
      : []),
    ...(upholstery
      ? [
          {
            label: upholstery.name,
            leading: (
              <ColourSwatch name={upholstery.name} hex={upholstery.interiorColorHex} size="dot" />
            ),
          },
        ]
      : []),
    ...(extrasCount > 0
      ? [{caption: t('entity.newVehicle.labels.extras', 'Extras'), label: String(extrasCount)}]
      : []),
  ];

  const previousIndex = props.currentStepIndex > 0 ? props.currentStepIndex - 1 : undefined;
  const nextStep = props.steps[props.currentStepIndex + 1];
  const nextIndex = nextStep ? props.currentStepIndex + 1 : undefined;
  const navigation: Action[] =
    props.hideStepNavigation || previousIndex === undefined
      ? []
      : [
          {
            type: 'button',
            variant: 'secondary',
            title: t('general.actions.back', 'Back'),
            leftIcon: 'navigation/arrow_back',
            onClick: () => props.onStepChange(previousIndex),
          },
        ];
  const trailing: Action[] = [
    ...(props.trailingActions ?? []),
    ...(props.hideStepNavigation || nextIndex === undefined
      ? []
      : [
          {
            type: 'button' as const,
            variant: 'primary' as const,
            title: t('general.actions.continue', 'Continue'),
            rightIcon: 'navigation/arrow_forward' as const,
            isDisabled: Boolean(props.blocker) || Boolean(nextStep?.isDisabled),
            onClick: () => props.onStepChange(nextIndex),
          },
        ]),
  ];

  return (
    <VStack spacing={5}>
      <ConfiguratorStepper
        steps={props.steps}
        currentIndex={props.currentStepIndex}
        onStepChange={props.onStepChange}
        data-testid="new-vehicle-configurator"
      />
      <Show when={props.banner}>{props.banner}</Show>
      <ConfiguratorHero
        makeName={props.context.makeName}
        modelName={props.context.modelName}
        trimName={trim?.name}
        modelYearName={props.context.modelYearName}
        status={props.status}
        chips={chips}
        placeholder={t('entity.newVehicle.labels.pickMakeModel', 'Pick a make and model to start')}
        hint={
          trim
            ? undefined
            : t(
                'entity.newVehicle.hints.buildSpec',
                'Pick a trim, engine, paint and interior to build the specification.'
              )
        }
      />
      <Show when={caption}>
        {(priceCaption) => (
          <Text
            size="xSmall"
            color="secondary"
            align="right"
            data-testid="new-vehicle-configurator-priceBasis"
          >
            {priceCaption.basis === 'gross'
              ? i18n.t('entity.newVehicle.labels.priceBasisGross', {
                  defaultValue: 'Prices incl. VAT {rate} %',
                  rate: priceCaption.rate,
                })
              : i18n.t('entity.newVehicle.labels.priceBasisNet', {
                  defaultValue: 'Prices excl. VAT (net)',
                })}
          </Text>
        )}
      </Show>
      <HStack spacing={5} align="flex-start">
        <Box flex={1} minWidth={0}>
          {props.hideRail ? (
            props.children
          ) : (
            <Box
              backgroundColor="general.white"
              border="1px solid"
              borderColor="general.separator"
              borderRadius="large"
              padding={6}
            >
              {props.children}
            </Box>
          )}
        </Box>
        <Show when={!props.hideRail}>
          <Box width={[72, 72, 76, 84]} flexShrink={0}>
            <SummaryRail
              rows={[
                {label: t('entity.newVehicle.labels.trimLevel', 'Trim level'), value: trim?.name},
                {
                  label: t('entity.newVehicle.labels.engineDrive', 'Engine & drive'),
                  value: engine ? `${engine.powerKw} kW · ${engine.name}` : undefined,
                },
                {label: t('entity.newVehicle.labels.colour', 'Colour'), value: colour?.name},
                {
                  label: t('entity.newVehicle.labels.upholstery', 'Upholstery'),
                  value: upholstery?.name,
                },
                {
                  label: t('entity.newVehicle.labels.optionsPackagesCount', 'Options · packages'),
                  value: `${optionFeatureCount} · ${props.packageIds.length}`,
                },
              ]}
              price={{
                ...railPrice,
                currency,
                canViewCost: props.canViewCost,
                vatRateLabel: !isNil(rate)
                  ? i18n.t('entity.newVehicle.labels.vatRate', {
                      defaultValue: 'VAT {rate} %',
                      rate,
                    })
                  : undefined,
              }}
            />
          </Box>
        </Show>
      </HStack>
      <ConfiguratorPriceBar
        {...barPrice}
        currency={currency}
        hint={props.blocker}
        leadingActions={[...(props.leadingActions ?? []), ...navigation]}
        trailingActions={trailing}
        data-testid="new-vehicle-configurator"
      />
    </VStack>
  );
}
